import React, { useState, useEffect, useMemo, useCallback } from "react";
import useLiveData from "../../realtime/useLiveData";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TablePagination,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import ViewAgendaOutlinedIcon from "@material-ui/icons/ViewAgendaOutlined";
import TableChartOutlinedIcon from "@material-ui/icons/TableChartOutlined";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import { getCurrencySymbol } from "../../utils/currency";
import { useSnackbar } from "../../utils/useSnackbar";
import * as api from "../../api";
import NoData from "../svgIcons/NoData";
import { ListPageSkeleton } from "../Skeletons";

import {
  DATE_PRESETS,
  getDateRangeFromPreset,
} from "./ProgramLogConstants";
import ProgramLogMetrics from "./ProgramLogMetrics";
import ProgramLogFilters from "./ProgramLogFilters";
import ProgramLogTable from "./ProgramLogTable";
import ProgramLogCardList from "./ProgramLogCardList";
import ProgramLogRetentionControl from "./ProgramLogRetentionControl";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
    marginTop: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1),
      gap: theme.spacing(0.5),
    },
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#0f172a",
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  titleIcon: {
    fontSize: 24,
    color: "#ef7f1a",
  },
  subtitle: {
    fontSize: "0.75rem",
    color: "#64748b",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  viewModeSwitch: {
    display: "flex",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#ffffff",
  },
  viewModeBtn: {
    padding: "4px 8px",
    minWidth: "auto",
    borderRadius: 0,
    color: "#64748b",
    fontSize: "0.85rem",
    "&$viewModeBtnActive": {
      backgroundColor: "#f1f5f9",
      color: "#0f172a",
      fontWeight: 700,
    },
  },
  viewModeBtnActive: {},
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: theme.spacing(4, 2),
    margin: theme.spacing(1, 0),
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px dashed #cbd5e1",
    textAlign: "center",
    flex: 1,
    overflow: "auto",
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#1e293b",
    margin: "0.5rem 0",
  },
  emptySubtitle: {
    fontSize: "0.8rem",
    color: "#64748b",
    maxWidth: 400,
    margin: "0.5rem 0",
  },
  contentWrapper: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minHeight: 0,
  },
}));

export default function ProgramLogs() {
  const classes = useStyles();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const profile = useEffectiveProfile();
  const currencySymbol = getCurrencySymbol(profile?.currency);
  const [forcedViewMode, setForcedViewMode] = useState(null); // 'card' | 'table' | null (auto)

  const defaultRange = useMemo(() => getDateRangeFromPreset(DATE_PRESETS.last7), []);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [referenceInput, setReferenceInput] = useState("");
  const [filterReference, setFilterReference] = useState("");
  const [dateSort, setDateSort] = useState("default");
  const [datePreset, setDatePreset] = useState(DATE_PRESETS.last7);
  const [startDate, setStartDate] = useState(defaultRange?.startDate || "");
  const [endDate, setEndDate] = useState(defaultRange?.endDate || "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  // SuperAdmin's Global Settings > Activity Logs toggle. SuperAdmin is
  // exempt (this component is also rendered as the SuperAdmin panel's own
  // Activity Logs tab - see Subscription.js - so they always keep access
  // to audit and to see the effect of the toggle they just flipped).
  // Fails open (visible) while loading/on fetch error, same convention as
  // the NavBar link this page is reached from.
  const cachedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("profile"));
    } catch {
      return null;
    }
  }, []);
  const isSuperAdmin = cachedUser?.result?.userType?.includes("superAdmin");
  const [visibilityChecked, setVisibilityChecked] = useState(
    isSuperAdmin || profile?.activityLogsVisible !== undefined
  );
  const [activityLogsVisible, setActivityLogsVisible] = useState(
    profile?.activityLogsVisible !== false
  );

  useEffect(() => {
    if (profile?.activityLogsVisible !== undefined) {
      setActivityLogsVisible(profile.activityLogsVisible !== false);
      setVisibilityChecked(true);
    }
  }, [profile?.activityLogsVisible]);

  useEffect(() => {
    if (isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getGlobalSettings();
        if (!cancelled && data?.data?.activityLogsVisible !== undefined) {
          setActivityLogsVisible(data.data.activityLogsVisible !== false);
        }
      } catch (error) {
        // Fall back to profile
      } finally {
        if (!cancelled) setVisibilityChecked(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounces the reference text search
  useEffect(() => {
    const t = setTimeout(() => {
      setFilterReference(referenceInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [referenceInput]);

  const effectiveDateRange = useMemo(() => {
    if (datePreset === DATE_PRESETS.custom) {
      return { startDate, endDate };
    }
    const range = getDateRangeFromPreset(datePreset);
    return range || { startDate, endDate };
  }, [datePreset, startDate, endDate]);

  // silent: live background re-read (useLiveData below) - no loader, and a
  // failed background read keeps what is already on screen.
  const loadLogs = useCallback(async ({ silent = false } = {}) => {
    const cachedProfile = JSON.parse(localStorage.getItem("profile") || "null");
    if (!cachedProfile) {
      history.push("/login");
      return;
    }
    if (!silent) setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        startDate: effectiveDateRange.startDate,
        endDate: effectiveDateRange.endDate,
      };
      if (filterUserId) params.userId = filterUserId;
      if (filterModule) params.module = filterModule;
      if (filterAction) params.action = filterAction;
      if (filterRole) params.userRole = filterRole;
      if (filterReference) params.reference = filterReference;
      if (dateSort !== "default") params.dateSort = dateSort;

      const [logsRes, usersRes] = await Promise.all([
        api.getProgramLogs(params),
        api.fetchUsers(),
      ]);
      const data = logsRes.data || {};
      const logsList = Array.isArray(data.logs) ? data.logs : [];
      setLogs(logsList);
      setTotalCount(data.totalCount ?? logsList.length);
      // Never offer a SuperAdmin account in the User filter for non-SuperAdmin
      // viewers - their logs are already excluded server-side, so letting the
      // dropdown list them here would only reveal the account exists.
      const allUsers = usersRes.data || [];
      setUsers(
        isSuperAdmin
          ? allUsers
          : allUsers.filter((u) => !u.userType?.includes?.("superAdmin"))
      );
    } catch (err) {
      if (silent) return;
      setLogs([]);
      setTotalCount(0);
      openSnackbar(err?.response?.data?.message || "Failed to load program logs");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    history,
    page,
    rowsPerPage,
    effectiveDateRange.startDate,
    effectiveDateRange.endDate,
    filterUserId,
    filterModule,
    filterAction,
    filterRole,
    filterReference,
    dateSort,
  ]);

  // Live: new activity from any user shows up here without a refresh.
  useLiveData(["programLogs"], () => loadLogs({ silent: true }), { minIntervalMs: 5000 });

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleResetFilters = () => {
    setFilterModule("");
    setFilterAction("");
    setFilterUserId("");
    setFilterRole("");
    setReferenceInput("");
    setFilterReference("");
    setDatePreset(DATE_PRESETS.last7);
    const r = getDateRangeFromPreset(DATE_PRESETS.last7);
    if (r) {
      setStartDate(r.startDate);
      setEndDate(r.endDate);
    }
    setPage(0);
  };

  const activeViewMode = forcedViewMode || "table";

  // Note: Activity Logs are available to all users, but normal users only see their own logs
  // The server enforces this via role-based filtering in getProgramLogs

  if (!isSuperAdmin && visibilityChecked && !activityLogsVisible) {
    return (
      <Box className={classes.root}>
        <Paper elevation={0} className={classes.emptyState}>
          <NoData />
          <Typography className={classes.emptyTitle}>
            Activity Logs Unavailable
          </Typography>
          <Typography className={classes.emptySubtitle}>
            This feature has been turned off for your account. Contact your administrator if you believe this is a mistake.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* 1. Page Header */}
      <Box className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography variant="h5" className={classes.title}>
            <ListAltOutlinedIcon className={classes.titleIcon} />
            Activity Logs
          </Typography>
          <Typography className={classes.subtitle}>
            Comprehensive audit trail tracking all user actions and system activities across all modules
          </Typography>
        </div>

        {/* View Mode Toggle (Card vs Table) on medium/large screens */}
        <div className={classes.headerActions}>
          {isSuperAdmin && <ProgramLogRetentionControl />}
          <div className={classes.viewModeSwitch}>
            <Tooltip title="Table view">
              <Button
                size="small"
                className={`${classes.viewModeBtn} ${activeViewMode === "table" ? classes.viewModeBtnActive : ""}`}
                onClick={() => setForcedViewMode("table")}
              >
                <TableChartOutlinedIcon style={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
            <Tooltip title="Card feed view">
              <Button
                size="small"
                className={`${classes.viewModeBtn} ${activeViewMode === "card" ? classes.viewModeBtnActive : ""}`}
                onClick={() => setForcedViewMode("card")}
              >
                <ViewAgendaOutlinedIcon style={{ fontSize: 18 }} />
              </Button>
            </Tooltip>
          </div>
        </div>
      </Box>

      <div className={classes.contentWrapper}>
        {/* 2. Executive KPI Metrics Cards */}
        <ProgramLogMetrics
          logs={logs}
          totalCount={totalCount}
          selectedModule={filterModule}
          onSelectModule={(mod) => {
            setFilterModule(mod);
            setPage(0);
          }}
          loading={loading}
        />

        {/* 3. Responsive Filter Toolbar */}
        <ProgramLogFilters
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        filterModule={filterModule}
        setFilterModule={setFilterModule}
        filterAction={filterAction}
        setFilterAction={setFilterAction}
        filterUserId={filterUserId}
        setFilterUserId={setFilterUserId}
        filterRole={filterRole}
        setFilterRole={setFilterRole}
        referenceInput={referenceInput}
        setReferenceInput={setReferenceInput}
        users={users}
        loading={loading}
        onRefresh={loadLogs}
        onResetFilters={handleResetFilters}
        setPage={setPage}
        isSuperAdmin={isSuperAdmin}
      />

      {/* 4. Main Log Content */}
      {loading ? (
        <ListPageSkeleton rows={6} columns={6} showToolbar={false} />
      ) : logs.length === 0 ? (
        <Paper elevation={0} className={classes.emptyState}>
          <NoData />
          <Typography className={classes.emptyTitle}>
            No Activity Logs Found
          </Typography>
          <Typography className={classes.emptySubtitle}>
            {filterModule || filterAction || filterUserId || filterRole || referenceInput
              ? "No activity logs match your current filter criteria. Try broadening your search or resetting filters."
              : "System activity logs for invoices, stock adjustments, and item catalog operations will appear here."}
          </Typography>
          {(filterModule || filterAction || filterUserId || filterRole || referenceInput) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RotateLeftIcon />}
              onClick={handleResetFilters}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              Reset All Filters
            </Button>
          )}
        </Paper>
      ) : activeViewMode === "card" ? (
        <>
          <ProgramLogCardList
            logs={logs}
            expandedRows={expandedRows}
            onToggleRow={toggleRow}
            currencySymbol={currencySymbol}
          />
          {totalCount > 0 && (
            <Paper elevation={0} style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Per page:"
              />
            </Paper>
          )}
        </>
      ) : (
        <ProgramLogTable
          logs={logs}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          dateSort={dateSort}
          onDateSortChange={(v) => {
            setDateSort(v);
            setPage(0);
          }}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          expandedRows={expandedRows}
          onToggleRow={toggleRow}
          currencySymbol={currencySymbol}
        />
      )}
      </div>
    </Box>
  );
}
